import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import Logo from "../../assets/Logo(500x500).png";
import AuthPanel from "../../components/AuthPanel/AuthPanel";

import { environment } from "../../environments/environment";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { useAuthContext } from "../../hooks/useAuthContext";

const EyeIcon = ({ show }) =>
  show ? (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  ) : (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );

export default function Login() {
  document.title = "Log in | LeadZLab";

  const { dispatch, user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const formInput = new FormData(event.currentTarget);
    try {
      const { data } = await axios.post(
        `${environment.BACKEND}/user/auth/login`,
        { email: formInput.get("email"), password: formInput.get("password") },
        { headers: { "Content-Type": "application/json" } }
      );
      toast.success("Welcome back!");
      Cookies.set("user", JSON.stringify(data.data), { expires: 365 });
      dispatch({ type: "LOGIN", payload: data.data });
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      <AuthPanel />

      {/* Right: form - more space, less congested */}
      <div className="flex w-full flex-col items-center justify-center overflow-auto px-8 py-8 lg:w-[520px] lg:flex-shrink-0 lg:overflow-hidden lg:px-12">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden">
            <NavLink to="/" className="inline-flex">
              <img
                src={Logo}
                alt="LeadZLab"
                className="h-8 w-auto rounded-lg"
              />
            </NavLink>
          </div>
          <h1 className="mt-8 text-xl font-bold tracking-tight text-slate-900 lg:mt-0">
            Log in
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            No account?{" "}
            <NavLink
              to="/signup"
              className="font-medium text-emerald-600 hover:text-emerald-700"
            >
              Sign up
            </NavLink>
          </p>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-slate-700"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-700"
                >
                  Password
                </label>
                <NavLink
                  to="/forgot-password"
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
                >
                  Forgot?
                </NavLink>
              </div>
              <div className="relative mt-1.5">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  className="block w-full rounded-lg border border-slate-300 bg-white py-3 pl-3 pr-10 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon show={showPassword} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-70"
            >
              {loading ? (
                <ThreeDots
                  height={20}
                  width={20}
                  color="#fff"
                  radius={4}
                  ariaLabel="loading"
                />
              ) : (
                "Log in"
              )}
            </button>
          </form>

          <p className="mt-6 text-center">
            <NavLink
              to="/"
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              ← Back to home
            </NavLink>
          </p>
        </div>
      </div>
    </div>
  );
}
